import React, { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import abrangencia from '../services/abrangencia';
import type { Source } from '../services/abrangencia';

interface IAbrangenciaContext {
  data: Source[] | null;
  loading: boolean;
  error: string | null;
  fetchAbrangencia: () => Promise<void>;
  ensureFetched: () => void;
}

const AbrangenciaContext = createContext<IAbrangenciaContext | undefined>(undefined);

export const useAbrangencia = () => {
  const context = useContext(AbrangenciaContext);
  if (!context) {
    throw new Error('useAbrangencia must be used within an AbrangenciaProvider');
  }

  React.useEffect(() => {
    context.ensureFetched();
  }, [context.ensureFetched]);

  return context;
};

interface AbrangenciaProviderProps {
  children: ReactNode;
}

export const AbrangenciaProvider: React.FC<AbrangenciaProviderProps> = ({ children }) => {
  const [data, setData] = useState<Source[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const fetchRequestedRef = useRef(false);

  const fetchAbrangencia = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await abrangencia.getAvailable();
      if (response.success) {
        setData(response.data);
      } else {
        setError('Erro ao buscar abrangências');
      }
    } catch (err: unknown) {
      const axiosMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(axiosMessage || 'Erro ao buscar abrangências');
      console.error('Erro ao buscar abrangências:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const ensureFetched = useCallback(() => {
    if (fetchRequestedRef.current) return;
    fetchRequestedRef.current = true;
    void fetchAbrangencia();
  }, [fetchAbrangencia]);

  return (
    <AbrangenciaContext.Provider
      value={{
        data,
        loading,
        error,
        fetchAbrangencia,
        ensureFetched,
      }}
    >
      {children}
    </AbrangenciaContext.Provider>
  );
};
